import React, { useState, useEffect } from "react";
import { getCandidateRefferences, createCandidateRefference, updateCandidateRefference, deleteCandidateRefference } from "@/api/candidateRefferences.jsx";
import { Button } from "@/components/ui/button.tsx";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card.tsx";
import { Input } from "@/components/ui/input.tsx";
import { Label } from "@/components/ui/label.tsx";
import { Textarea } from "@/components/ui/textarea.tsx";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Phone, Mail, User } from "lucide-react";

export default function CandidateReferences({ candidateId, candidateName }) {
    const [references, setReferences] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingReference, setEditingReference] = useState(null);
    const [formData, setFormData] = useState({
        refereeName: '',
        refereePhone: '',
        refereeEmail: '',
        notes: ''
    });

    useEffect(() => {
        loadReferences();
    }, [candidateId]);

    const loadReferences = async () => {
        setLoading(true);
        try {
            const data = await getCandidateRefferences(candidateId);
            setReferences(data || []);
        } catch (error) {
            console.error("Error loading references:", error);
            toast.error("Failed to load references");
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            refereeName: '',
            refereePhone: '',
            refereeEmail: '',
            notes: ''
        });
        setEditingReference(null);
        setShowAddForm(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        try {
            if (editingReference) {
                await updateCandidateRefference(candidateId, editingReference.id, formData);
                toast.success("Reference updated successfully");
            } else {
                await createCandidateRefference(candidateId, formData);
                toast.success("Reference added successfully");
            }
            
            resetForm();
            loadReferences();
        } catch (error) {
            toast.error(`Failed to ${editingReference ? 'update' : 'add'} reference`);
        }
    };

    const handleEdit = (reference) => {
        setFormData({
            refereeName: reference.refereeName || '',
            refereePhone: reference.refereePhone || '',
            refereeEmail: reference.refereeEmail || '',
            notes: reference.notes || ''
        });
        setEditingReference(reference);
        setShowAddForm(true);
    };

    const handleDelete = async (referenceId) => {
        if (!confirm("Are you sure you want to delete this reference?")) {
            return;
        }

        try {
            await deleteCandidateRefference(candidateId, referenceId);
            toast.success("Reference deleted successfully");
            loadReferences();
        } catch (error) {
            toast.error("Failed to delete reference");
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    if (loading) {
        return <div className="text-center py-4">Loading references...</div>;
    }

    return (
        <div className="space-y-6">
            {/* Add/Edit Reference Form */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                        <span>{editingReference ? 'Edit Reference' : 'Add Reference'}</span>
                        <Button
                            size="sm"
                            onClick={() => setShowAddForm(!showAddForm)}
                            className="flex items-center gap-2"
                        >
                            <Plus className="h-4 w-4" />
                            Add Reference
                        </Button>
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {showAddForm && (
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <Label htmlFor="refereeName">Referee Name</Label>
                                    <Input
                                        id="refereeName"
                                        name="refereeName"
                                        value={formData.refereeName}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="refereePhone">Phone Number</Label>
                                    <Input
                                        id="refereePhone"
                                        name="refereePhone"
                                        type="tel"
                                        value={formData.refereePhone}
                                        onChange={handleInputChange}
                                    />
                                </div>
                            </div>
                            
                            <div>
                                <Label htmlFor="refereeEmail">Email Address</Label>
                                <Input
                                    id="refereeEmail"
                                    name="refereeEmail"
                                    type="email"
                                    value={formData.refereeEmail}
                                    onChange={handleInputChange}
                                />
                            </div>

                            <div>
                                <Label htmlFor="notes">Notes</Label>
                                <Textarea
                                    id="notes"
                                    name="notes"
                                    value={formData.notes}
                                    onChange={handleInputChange}
                                    rows={3}
                                />
                            </div>

                            <div className="flex items-center gap-2">
                                <Button type="submit">
                                    {editingReference ? 'Update Reference' : 'Add Reference'}
                                </Button>
                                <Button type="button" variant="outline" onClick={resetForm}>
                                    Cancel
                                </Button>
                            </div>
                        </form>
                    )}
                </CardContent>
            </Card>

            {/* References List */}
            <Card>
                <CardHeader>
                    <CardTitle>References</CardTitle>
                </CardHeader>
                <CardContent>
                    {references.length > 0 ? (
                        <div className="space-y-4">
                            {references.map((reference) => (
                                <div key={reference.id} className="border rounded-lg p-4 bg-gray-50">
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-2">
                                                <User className="h-4 w-4 text-gray-500" />
                                                <h4 className="font-medium text-lg">{reference.refereeName}</h4>
                                            </div>
                                            
                                            <div className="space-y-2 text-sm text-gray-600">
                                                {reference.refereePhone && (
                                                    <div className="flex items-center gap-2">
                                                        <Phone className="h-4 w-4" />
                                                        <a href={`tel:${reference.refereePhone}`} className="hover:text-blue-600">
                                                            {reference.refereePhone}
                                                        </a>
                                                    </div>
                                                )}
                                                
                                                {reference.refereeEmail && (
                                                    <div className="flex items-center gap-2">
                                                        <Mail className="h-4 w-4" />
                                                        <a href={`mailto:${reference.refereeEmail}`} className="hover:text-blue-600">
                                                            {reference.refereeEmail}
                                                        </a>
                                                    </div>
                                                )}
                                                
                                                {reference.notes && (
                                                    <div className="mt-3">
                                                        <p className="text-gray-700">{reference.notes}</p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        
                                        <div className="flex items-center gap-2 ml-4">
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => handleEdit(reference)}
                                                className="flex items-center gap-1"
                                            >
                                                <Edit className="h-3 w-3" />
                                                Edit
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="destructive"
                                                onClick={() => handleDelete(reference.id)}
                                                className="flex items-center gap-1"
                                            >
                                                <Trash2 className="h-3 w-3" />
                                                Delete
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-8 text-gray-500">
                            <User className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                            <p className="text-lg font-medium mb-2">No references added</p>
                            <p className="text-sm">Add references for {candidateName} to see them here.</p>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}